/* eslint-disable no-console */

import { register } from 'register-service-worker'
import clientVersion from '@/utils/clientVersion'

const UPDATE_CHECK_MS = 60 * 1000
// Together these stay well under the 15s after which App.vue reports the refresh failed.
const SWITCH_TIMEOUT_MS = 3000
const CLEAR_TIMEOUT_MS = 5000
const APP_BUNDLE = /\/js\/app\.[a-z0-9]+\.js/

let registration = null

// Compares the app bundle this tab runs with the one in the live index.html,
// so a swapped dist is noticed without waiting on the service worker.
export function checkForNewBuild() {
  const running = Array.prototype.map
    .call(document.scripts, (s) => s.src)
    .map((src) => (src.match(APP_BUNDLE) || [])[0])
    .find(Boolean)
  if (!running) return Promise.resolve(false)

  // The query string keeps the service worker's precached index.html from
  // answering this request.
  const url = `${process.env.BASE_URL}index.html?v=${Date.now()}`
  return fetch(url, { cache: 'no-store' })
    .then((res) => (res.ok ? res.text() : ''))
    .then((html) => {
      const live = (html.match(APP_BUNDLE) || [])[0]
      if (live && live !== running) {
        clientVersion.updateAvailable = true
        return true
      }
      return false
    })
    .catch(() => false)
}

function flagIfUpdateWaiting() {
  if (
    registration &&
    registration.waiting &&
    navigator.serviceWorker.controller
  ) {
    clientVersion.updateAvailable = true
  }
}

// No waiting worker to switch to, or the switch stalled: drop the old worker and
// its caches so the reload is served the new build.
function clearAndReload() {
  const unregister = registration ? registration.unregister() : null
  const clearCaches = window.caches
    ? window.caches
        .keys()
        .then((keys) => Promise.all(keys.map((k) => window.caches.delete(k))))
    : null
  const cleared = Promise.all([unregister, clearCaches])
    .catch(() => {})
    // Safari can reload this page from its HTTP cache (the old build), so replace that copy first.
    .then(() => fetch(window.location.href, { cache: 'reload' }))
    .catch(() => {})
  // Any step above can hang on a device, and the reload must still happen.
  const timeLimit = new Promise((resolve) =>
    setTimeout(resolve, CLEAR_TIMEOUT_MS),
  )
  Promise.race([cleared, timeLimit]).then(() => window.location.reload())
}

export function applyUpdate() {
  const waiting = registration && registration.waiting
  // An uncontrolled tab never gets controllerchange, as the worker doesn't claim clients.
  if (!waiting || !navigator.serviceWorker.controller) {
    clearAndReload()
    return
  }
  const fallback = setTimeout(clearAndReload, SWITCH_TIMEOUT_MS)
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    clearTimeout(fallback)
    window.location.reload()
  })
  waiting.postMessage({ type: 'SKIP_WAITING' })
}

if (process.env.NODE_ENV === 'production') {
  register(`${process.env.BASE_URL}service-worker.js`, {
    ready() {
      console.log(
        'App is being served from cache by a service worker.\n' +
          'For more details, visit https://goo.gl/AFskqB',
      )
    },
    registered(reg) {
      console.log('Service worker has been registered.')
      registration = reg
      flagIfUpdateWaiting()
      const checkForUpdate = () => {
        reg.update().catch(() => {})
        checkForNewBuild()
      }
      setInterval(() => {
        if (!document.hidden) checkForUpdate()
      }, UPDATE_CHECK_MS)
      window.addEventListener('focus', checkForUpdate)
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) checkForUpdate()
      })
    },
    cached() {
      console.log('Content has been cached for offline use.')
    },
    updatefound() {
      console.log('New content is downloading.')
    },
    updated(reg) {
      console.log('New content is available; prompting the user to refresh.')
      registration = reg
      clientVersion.updateAvailable = true
    },
    offline() {
      console.log(
        'No internet connection found. App is running in offline mode.',
      )
    },
    error(error) {
      console.error('Error during service worker registration:', error)
    },
  })
}
